import { useCallback, useEffect, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "cloudox.theme";

function readStoredPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // SSR / locked-down browser: fall through.
  }
  return "system";
}

function computeEffective(pref: ThemePreference): EffectiveTheme {
  if (pref === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return pref;
}

/**
 * Theme state with three values: `light`, `dark`, `system`.
 *
 * Mirrors the inline bootstrap in `index.html` so the initial paint is
 * already correct; this hook is the *runtime* mirror.
 */
export function useTheme(): {
  preference: ThemePreference;
  effective: EffectiveTheme;
  setPreference: (next: ThemePreference) => void;
  cycle: () => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [effective, setEffective] = useState<EffectiveTheme>(() => computeEffective(readStoredPreference()));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", preference);
    const next = computeEffective(preference);
    setEffective(next);
    document.documentElement.setAttribute("data-effective-theme", next);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // best effort
    }
  }, [preference]);

  // Track system changes only when the user picked "system".
  useEffect(() => {
    if (preference !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const next = media.matches ? "dark" : "light";
      setEffective(next);
      document.documentElement.setAttribute("data-effective-theme", next);
    };
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => setPreferenceState(next), []);
  const cycle = useCallback(() => {
    setPreferenceState((prev) => (prev === "light" ? "dark" : prev === "dark" ? "system" : "light"));
  }, []);

  return { preference, effective, setPreference, cycle };
}
